import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import {
  COMMANDE_STATUT_LABELS,
  FACTURE_STATUT_LABELS,
  RAISON_MOUVEMENT_LABELS,
  type CommandeStatut,
  type FactureStatut,
  type RaisonMouvement,
} from '@sirajoptic/shared';
import { dh } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Le rouge et le vert sont les seules teintes admises hors moutarde, et
 * uniquement sur les badges — c'est la règle de la section 9.
 */
const variantes = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      ton: {
        neutre: 'bg-surface-sunken text-ink-soft',
        moutarde: 'bg-mustard-100 text-mustard-800',
        noir: 'bg-ink text-white',
        succes: 'bg-success-soft text-success',
        danger: 'bg-danger-soft text-danger',
        info: 'bg-info-soft text-info',
      },
    },
    defaultVariants: { ton: 'neutre' },
  },
);

export type BadgeProps = VariantProps<typeof variantes> & {
  children: ReactNode;
  className?: string;
  title?: string;
};

export function Badge({ ton, children, className, title }: BadgeProps) {
  return (
    <span title={title} className={cn(variantes({ ton }), className)}>
      {children}
    </span>
  );
}

const TON_COMMANDE: Record<CommandeStatut, BadgeProps['ton']> = {
  EN_COURS: 'moutarde',
  LIVREE: 'succes',
  ANNULEE: 'danger',
};

export function BadgeStatutCommande({ statut }: { statut: CommandeStatut }) {
  return <Badge ton={TON_COMMANDE[statut]}>{COMMANDE_STATUT_LABELS[statut]}</Badge>;
}

const TON_FACTURE: Record<FactureStatut, BadgeProps['ton']> = {
  BROUILLON: 'neutre',
  EMISE: 'succes',
  ANNULEE: 'danger',
};

export function BadgeStatutFacture({ statut }: { statut: FactureStatut }) {
  return <Badge ton={TON_FACTURE[statut]}>{FACTURE_STATUT_LABELS[statut]}</Badge>;
}

const TON_MOUVEMENT: Record<RaisonMouvement, BadgeProps['ton']> = {
  ACHAT: 'succes',
  VENTE: 'moutarde',
  CORRECTION: 'info',
  RETOUR: 'neutre',
  ANNULATION: 'danger',
};

export function BadgeMouvement({ raison }: { raison: RaisonMouvement }) {
  return <Badge ton={TON_MOUVEMENT[raison]}>{RAISON_MOUVEMENT_LABELS[raison]}</Badge>;
}

/**
 * Reste dû : vert quand c'est soldé, ambre quand il reste à payer, bleu en cas
 * de trop-perçu — un reste négatif est une situation valide (règle 9).
 */
export function BadgeReste({ reste }: { reste: number }) {
  if (Math.abs(reste) < 0.005) return <Badge ton="succes">Soldée</Badge>;
  if (reste > 0) return <Badge ton="moutarde">{dh(reste)}</Badge>;
  return (
    <Badge ton="info" title="Trop-perçu">
      {dh(reste)}
    </Badge>
  );
}

/** Signale une ligne importée dont un champ demande une correction. */
export function BadgeAVerifier({ motifs }: { motifs?: string[] }) {
  return (
    <Badge ton="danger" title={motifs?.join(' · ')}>
      À vérifier
    </Badge>
  );
}
